import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from "react-bootstrap";
import { FaDownload, FaMailBulk, FaSync, FaUpload, FaVoicemail } from "react-icons/fa";
import StethoscopeLoading from './StethoscopeLoading/StethoscopeLoading';
import { AgGridReact } from "ag-grid-react";
import mammoth from "mammoth";
import { CTSearchView } from "."; // Assuming CTSearchView is exported from src/components/index.js
import ReportPreview from "./previewControls"; // Assuming ReportPreview is in the same directory or a sub-directory
import './ReportDisplaySection.css';
import { buildPdfPreviewSrc, isDocxContentType, isDocxFileName, isErrorDocumentContentType, isImageContentType, isPdfContentType } from '../utils/utils';

/** Matches ReportDisplaySection.css: 6 × 0.48s (reduced-motion path uses a shorter soft blink) */
const HEADER_EMAIL_DOWNLOAD_BLINK_MS = 3200;

const ReportDisplaySection = ({
    // Search Props
    searchQuery, // Though not directly used if CTSearchView is uncontrolled and only onChange is needed
    onSearchChange,
    onSearchClear,
    onAdvanceSearchClick,
    // Header Action Props
    onIncludeArchivedToggle,
    onRefreshClick,
    // Upload button (Note: The original upload button in this section seems static, might need its own handler if it's functional)
    // Tab Props
    gridHeaders,
    activeTab,
    onTabClick,
    rowDatanew, // For tab counts
    // Grid Props
    gridRef,
    rowData,
    columnDefs,
    onRowClicked,
    // Preview Props
    previewContainerRef,
    isFullscreen,
    pdfUrl,
    selectedReport, // Data for the selected report, used to decide if iframe or placeholder is shown
    selectedReportName,
    selectedReportID,
    checkedReportIds, // Array of row indices for checked reports
    onFullScreenToggle,
    onDeleteReport,
    onArchiveReport,
    onArchiveUndo,
    onDownloadReport,
    onShareReport,
    onEditReport,
    iframeRef,
    onUploadReport, isSelectedBloodWork,
    onViewTrends,
    onCompareReports,
    isProvider,
    isAccess,
    contactName,
    handleNotes,
    includeArchive,
    selectedRow,
    isFromReceipts,
    totalAmount, showBlink,
    onSummary,
    contentType,
    onIncludeArchivedTogglewithlabel,
    handleClear,
    isReceipts,
    onEmailClick,
    onMultipleDownloadClick,
    selectedFamilyMember,
    isReportsGridLoading = false,
    isReportPreviewLoading = false,
    hidePdfToolbar = false,
    hideAdvancedSearchOption = false,
}) => {
    const [screenSize, setScreenSize] = useState('desktop');
    const [blinkEmailDownloadIcons, setBlinkEmailDownloadIcons] = useState(false);
    const [docxHtml, setDocxHtml] = useState('');
    const [docxPreviewError, setDocxPreviewError] = useState(null);
    const [isDocxPreviewLoading, setIsDocxPreviewLoading] = useState(false);
    const hadCheckedReportsRef = useRef(false);
    const categoryCountCacheRef = useRef(null);
    const rowsForCounts = Array.isArray(rowDatanew) ? rowDatanew : [];
    if (rowsForCounts.length > 0) {
        const nextCounts = { All: rowsForCounts.length };
        rowsForCounts.forEach((row) => {
            const name = row?.category;
            if (!name || name === 'All') return;
            nextCounts[name] = (nextCounts[name] || 0) + 1;
        });
        categoryCountCacheRef.current = nextCounts;
    }
    const displayCounts = rowsForCounts.length > 0
        ? categoryCountCacheRef.current
        : (isReportsGridLoading ? categoryCountCacheRef.current : null);
    const tabCount = (tabName) => {
        if (displayCounts) {
            return tabName === 'All' ? (displayCounts.All || 0) : (displayCounts[tabName] || 0);
        }
        return rowsForCounts.filter((row) => tabName === 'All' || row.category === tabName).length;
    };
    const selectedPreviewFileName = (
        selectedReport?.receipt_name ||
        selectedReport?.report_name ||
        selectedReport?.name ||
        selectedReportName ||
        ''
    ).toLowerCase();
    const isDocxPreview = isDocxContentType(contentType) || isDocxFileName(selectedPreviewFileName);
    const hasFilePreview = Boolean(pdfUrl) && !isErrorDocumentContentType(contentType);
    const displayRows = (Array.isArray(rowData) ? rowData : []).filter((row) => {
        if (!row) return false;
        if (isFromReceipts) return Boolean(row.receiptId || row.receipt_name || row.name);
        return Boolean(row.reportId || row.name);
    });

    // Resolve display name for the currently selected family member (if any).
    const familyMemberDisplayName = selectedFamilyMember?.dropdown_display_name
        || selectedFamilyMember?.relative_name
        || '';

    // Whenever the selected family member changes, ensure any local row selection in the
    // grid is cleared so we don't show stale highlighting carried over from another user.
    useEffect(() => {
        if (gridRef && gridRef.current && gridRef.current.api) {
            try {
                gridRef.current.api.deselectAll();
            } catch (e) {
                // grid may not be ready yet — safe to ignore
            }
        }
    }, [selectedFamilyMember?.user_id, gridRef]);

    const hasCheckedReports =
        Array.isArray(checkedReportIds) && checkedReportIds.length > 0;

    useLayoutEffect(() => {
        if (hasCheckedReports && !hadCheckedReportsRef.current) {
            setBlinkEmailDownloadIcons(true);
            hadCheckedReportsRef.current = true;
        } else if (!hasCheckedReports) {
            hadCheckedReportsRef.current = false;
        }
    }, [hasCheckedReports]);

    useEffect(() => {
        if (!blinkEmailDownloadIcons) return undefined;
        const id = window.setTimeout(
            () => setBlinkEmailDownloadIcons(false),
            HEADER_EMAIL_DOWNLOAD_BLINK_MS
        );
        return () => window.clearTimeout(id);
    }, [blinkEmailDownloadIcons]);

    useEffect(() => {
        if (!pdfUrl || !isDocxPreview) {
            setDocxHtml('');
            setDocxPreviewError(null);
            setIsDocxPreviewLoading(false);
            return undefined;
        }

        let isMounted = true;

        setDocxHtml('');
        setDocxPreviewError(null);
        setIsDocxPreviewLoading(true);

        fetch(pdfUrl)
            .then((response) => response.arrayBuffer())
            .then((arrayBuffer) => mammoth.convertToHtml({ arrayBuffer }))
            .then((result) => {
                if (isMounted) {
                    setDocxHtml(result.value || '<p>No preview content available.</p>');
                }
            })
            .catch((error) => {
                console.error('Error rendering DOCX preview:', error);
                if (isMounted) {
                    setDocxPreviewError('Unable to preview this DOCX file. Please download it to view.');
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsDocxPreviewLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [pdfUrl, isDocxPreview]);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setScreenSize('mobile');
            } else if (width < 1024) {
                setScreenSize('tablet');
            } else {
                setScreenSize('desktop');
            }
        };

        handleResize(); // Initial call
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Dynamic height calculation based on screen size and zoom level
    const getContainerHeight = () => {
        const zoomLevel = window.devicePixelRatio || 1;
      
        if (screenSize === 'mobile') {
            if (zoomLevel >= 1.5) {
                return 'calc(100vh - 180px)'; // Higher zoom needs more space
            }
            return 'calc(100vh - 160px)'; // Normal mobile
        } else if (screenSize === 'tablet') {
            if (zoomLevel >= 1.5) {
                return 'calc(100vh - 130px)'; // Higher zoom needs more space
            }
            return 'calc(100vh - 120px)'; // Normal tablet
        } else {

            if (zoomLevel >= 1 && zoomLevel <= 2) {
                console.log(zoomLevel, 'screenSize')
                return 'calc(100vh - 150px)'; // Higher zoom needs more space
            } else if (zoomLevel >= 2.1 && zoomLevel <= 2.9) {
                console.log(zoomLevel, 'screenSize2')
                return 'calc(100vh - 175px)'; // Normal desktop
            } else if (zoomLevel >= 3 && zoomLevel <= 3.9) {
                console.log(zoomLevel, 'screenSize3')
                return 'calc(100vh - 200px)'; // Normal desktop
            }else{
                return 'calc(100vh - 150px)'; // Normal desktop
            }
        }
    };

    return (
        <div className="col-12" style={{
            height: getContainerHeight(),
            display: 'flex',
            flexDirection: 'column',
            overflowY:'auto',
            overflowX:'hidden',
        }}>
            {/* Search Box */}
            <div style={{ padding: '2px 0', flexShrink: 0 }}>
                <CTSearchView
                    value={searchQuery || ""}
                    placeholder={isFromReceipts ? "Search receipts" : "Search reports"}
                    onChange={onSearchChange}
                    onChangeclear={onSearchClear}
                    advanceSearchClick={onAdvanceSearchClick}
                    showAdvanceSearch={!hideAdvancedSearchOption && !isAccess && !isFromReceipts}
                    showBlink={showBlink}
                />
            </div>

            {/* Header */}
            <div className='bg-white px-3 py-1' style={{
                paddingTop: isFromReceipts ? "6px" : "4px",
                backgroundColor: 'transparent',
                borderBottom: '1px solid #e5e7eb',
                flexShrink: 0
            }}>
                {!isProvider && !isAccess && (
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div className="d-flex align-items-center flex-wrap">
                            <input type="checkbox" id="includeArchived" className="me-2" onChange={onIncludeArchivedToggle} checked={!!includeArchive} />
                            <label htmlFor="includeArchived" className="me-3 fw-bold" style={{ color: 'red', fontSize: screenSize === 'mobile' ? '14px' : '16px' }}>
                                {isFromReceipts ? "Include Archived Receipts" : "Include Archived Reports"}
                            </label>
                            {familyMemberDisplayName && (
                                <span
                                    className="me-3 fw-bold text-truncate"
                                    style={{
                                        color: 'var(--pmr-primary)',
                                        fontSize: screenSize === 'mobile' ? '13px' : '15px',
                                        maxWidth: screenSize === 'mobile' ? '160px' : '260px'
                                    }}
                                    title={`Viewing ${isFromReceipts ? 'receipts' : 'reports'} for ${familyMemberDisplayName}`}
                                >
                                    Viewing: {familyMemberDisplayName}
                                </span>
                            )}
                            <Button variant="link" style={{ color: 'var(--pmr-secondary)', fontWeight: '600' }} className='text-decoration-none' onClick={onRefreshClick}>
                                <FaSync /> {screenSize !== 'mobile' && 'Refresh'}
                            </Button>
                            {hasCheckedReports && (
                                <>
                                    <Button
                                        variant="link"
                                        className="text-decoration-none report-header-action-btn report-header-action-btn--email"
                                        onClick={onEmailClick}
                                    >
                                        <FaMailBulk
                                            className={[
                                                'report-header-action-icon',
                                                blinkEmailDownloadIcons && 'report-header-action-icon--blink-email',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            aria-hidden
                                        />
                                        {screenSize !== 'mobile' && ' Email'}
                                    </Button>
                                    <Button
                                        variant="link"
                                        className="text-decoration-none report-header-action-btn report-header-action-btn--download"
                                        onClick={onMultipleDownloadClick}
                                    >
                                        <FaDownload
                                            className={[
                                                'report-header-action-icon',
                                                blinkEmailDownloadIcons &&
                                                    'report-header-action-icon--blink-download report-header-action-icon--blink-delay',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            aria-hidden
                                        />
                                        {screenSize !== 'mobile' && ' Download'}
                                    </Button>
                                </>
                            )}
                        </div>
                        <div className="mt-2 mt-md-0">
                            <Button variant="link" className="fw-bold text-decoration-none" onClick={onUploadReport}>
                                <span className="text-muted" style={{ fontSize: screenSize === 'mobile' ? '12px' : '14px' }}>
                                    {isFromReceipts ? "Have a receipt(s) to upload?" : "Have a report(s) to upload?"}
                                </span>
                                <span className="text-danger text-decoration-underline px-1" onClick={onUploadReport}>
                                    Click here
                                </span>
                            </Button>
                        </div>
                    </div>
                )}

                {isAccess && (
                    <div className="d-flex justify-content-between align-items-center">
                        {contactName && (
                            <div className="col-12 mb-2">
                                <div className="color-gray d-flex align-items-baseline gap-1 flex-wrap">
                                    <span> You are viewing </span>
                                    <span className="fw-bold" style={{ fontSize: screenSize === 'mobile' ? '1rem' : '1.2rem', color: 'var(--pmr-danger)' }}>
                                        {contactName}
                                    </span>
                                    <span> reports</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div style={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <ul className="nav border-0 d-flex flex-wrap" style={{ marginBottom: '0' }}>
                    {(gridHeaders || []).map((tab, index) => (
                        <li className="nav-item" key={index}>
                            <button
                                className="nav-link border-0"
                                style={{
                                    backgroundColor: 'transparent',
                                    color: activeTab === tab.name ? 'var(--pmr-primary)' : 'var(--pmr-text)',
                                    fontSize: screenSize === 'mobile' ? '13px' : '15px',
                                    fontWeight: '500',
                                    border: 'none',
                                    borderRadius: '0',
                                    padding: screenSize === 'mobile' ? '8px 12px' : '12px 20px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    outline: 'none',
                                    boxShadow: 'none',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => onTabClick(tab)}
                            >
                                <span className="me-1">{tab.name}</span>
                                <span className="fw-bold" style={{
                                    color: 'var(--pmr-secondary)',
                                    fontSize: screenSize === 'mobile' ? '13px' : '15px',
                                    paddingLeft: '5px',
                                    fontVariantNumeric: 'tabular-nums'
                                }}>
                                    ({tabCount(tab.name)})
                                </span>
                                {activeTab === tab.name && (
                                    <div className="bluegradient" style={{
                                        position: 'absolute',
                                        bottom: '1.5px',
                                        left: '0',
                                        right: '0',
                                        height: '3px',
                                        zIndex: 10
                                    }}></div>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Main Content Area - fills the space under the tabs for every account, including users with no reports */}
            <div className='px-2 py-1' style={{
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
            }}>
                <div className={`row ${screenSize === 'mobile' ? 'g-2' : 'mb-2'} px-2`} style={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    height: '100%',
                    alignItems: 'stretch',
                    flexWrap: screenSize === 'mobile' ? 'wrap' : 'nowrap'
                }}>
                    {/* Grid Column */}
                    <div className={`${screenSize === 'mobile' ? 'col-12 mb-2' : 'col-md-6 col-lg-6 col-sm-12 col-xs-12'} card-preview shadow-lg ag-theme-balham rounded-3 px-2`}
                        style={{
                            height: screenSize === 'mobile' ? '45vh' : '100%',
                            minHeight: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',

                        }}>
                        <div className="row" style={{ flex: '1', minHeight: 0, }}>
                            <div className="col-12 reports-grid-host" style={{
                                height: '100%',
                                overflow: 'auto',
                                position: 'relative'
                            }}>
                                {isReportsGridLoading && (
                                    <StethoscopeLoading
                                        overlay={rowData.length > 0}
                                    />
                                )}
                                {!isReportsGridLoading && displayRows.length === 0 ? (
                                    <div className="d-flex align-items-center justify-content-center h-100">
                                        <p className="text-danger fs-5 fw-bold">{isFromReceipts ? "No receipts to show" : "No reports to show"}</p>
                                    </div>
                                ) : (displayRows.length > 0) && (
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={displayRows}
                                        columnDefs={columnDefs}
                                        enableBrowserTooltip={true}
                                        tooltipShowDelay={0}
                                        animateRows={false}
                                        domLayout="normal"
                                        onRowClicked={onRowClicked}
                                        filter={true}
                                        sortingOrder={['asc', 'desc']}
                                        rowClass={(params) => {
                                            return params.rowIndex % 2 === 0 ? 'even-row' : 'odd-row';
                                        }}
                                        style={{ height: '100%', width: '100%' }}
                                        getRowStyle={(params) => {
                                            const isEven = params.rowIndex % 2 === 0;
                                            const isSelected = params.rowIndex === selectedRow;
                                            const isChecked = checkedReportIds && checkedReportIds.includes(params.rowIndex);
                                            const isNewattached = params.data && params.data.isNewattached;
                                            const isHighlight = params.data && params.data.isHighlight;
                                            const isNewOrHighlighted = !!(isNewattached || isHighlight);

                                            if (isSelected || isChecked) {
                                                return {
                                                    backgroundColor: 'var(--pmr-grid-row-hover)',
                                                    border: 'none',
                                                    borderBottom: '1px solid #dee2e6'
                                                };
                                            }
                                            if (isNewOrHighlighted) {
                                                return {
                                                    backgroundColor: '#ffe393',
                                                    border: 'none',
                                                    borderBottom: '1px solid #dee2e6'
                                                };
                                            }
                                            return {
                                                backgroundColor: isEven ? '#ffffff' : 'var(--pmr-bg-grid)',
                                                border: 'none',
                                                borderBottom: '1px solid #dee2e6'
                                            };
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Footer Buttons - Fixed at bottom */}
                        <div className="row" style={{ flexShrink: 0 }}>
                            <div className="col-12">
                                {isFromReceipts && (
                                    <div style={{
                                        width: '100%',
                                        backgroundColor: 'white',
                                        marginBottom: '10px',
                                        borderTop: '1px solid #e5e7eb',
                                        padding: '10px'
                                    }} className="d-flex justify-content-between py-2 flex-wrap">
                                        <span className="text-muted fw-bold" style={{ fontSize: screenSize === 'mobile' ? '12px' : '14px' }}>
                                            Total Items: {rowDatanew.length}
                                        </span>
                                        <span className="text-muted fw-bold" style={{ fontSize: screenSize === 'mobile' ? '12px' : '14px' }}>
                                            Total Amount: {totalAmount}
                                        </span>
                                    </div>
                                )}
                                {isSelectedBloodWork && !isFullscreen && (
                                    <div style={{
                                        width: '100%',
                                        backgroundColor: 'white',
                                        borderTop: '1px solid #e5e7eb'
                                    }} className="d-flex justify-content-between py-2 flex-wrap">
                                        <button
                                            className="btn btn-primary rounded-pill px-3 py-2 shadow-lg"
                                            style={{
                                                backgroundColor: "#0072ff",
                                                color: "white",
                                                fontSize: screenSize === 'mobile' ? "12px" : "16px",
                                                fontWeight: "bold"
                                            }}
                                            onClick={onViewTrends}
                                        >
                                            <i className="bi bi-graph-up-arrow me-1"></i>
                                            {screenSize !== 'mobile' && 'View Trends'}
                                        </button>
                                        <button
                                            className="btn btn-primary rounded-pill px-3 py-2 shadow-lg"
                                            style={{
                                                backgroundColor: "#0072ff",
                                                color: "white",
                                                fontSize: screenSize === 'mobile' ? "12px" : "16px",
                                                fontWeight: "bold"
                                            }}
                                            onClick={onCompareReports}
                                        >
                                            Compare
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Preview Column */}
                    <div
                        ref={previewContainerRef}
                        className={`${screenSize === 'mobile' ? 'col-12' : 'col-md-6 col-lg-6 col-sm-12 col-xs-12'} card-preview shadow-lg rounded-3`}
                        style={{
                            height: screenSize === 'mobile' ? '50vh' : '100%',
                            minHeight: 0,
                            alignSelf: 'stretch',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <div className="px-2 py-2" style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}>
                            {hasFilePreview && selectedReport && !isReportPreviewLoading && (
                                <div className="mb-2" style={{ flexShrink: 0 }}>
                                    <ReportPreview
                                        selectedReport={selectedReport}
                                        selectedReportName={selectedReportName}
                                        selectedReportID={selectedReportID}
                                        onFullScreenToggle={onFullScreenToggle}
                                        isFullscreen={isFullscreen}
                                        onDelete={onDeleteReport}
                                        onArchive={onArchiveReport}
                                        onDownload={onDownloadReport}
                                        onShare={onShareReport}
                                        onEdit={onEditReport}
                                        onArchiveUndo={onArchiveUndo}
                                        onSummary={onSummary}
                                        showEditButton={isAccess ? false : true}
                                        showNotesButton={isAccess ? false : true}
                                        showShareButton={isAccess ? false : true}
                                        handleNotes={handleNotes}
                                        isFromReceipts={isFromReceipts}
                                        isSelectedBloodWork={isSelectedBloodWork}
                                        handleClear={handleClear}
                                        isReceipts={isReceipts}
                                        includeArchive={includeArchive}
                                    />
                                </div>
                            )}

                            {isReportPreviewLoading ? (
                                <StethoscopeLoading
                                    style={{ flex: '1' }}
                                />
                            ) : hasFilePreview ? (
                                <div style={{
                                    flex: '1',

                                    minHeight: 0
                                }}>
                                    {(() => {
                                        const reportFileName = selectedPreviewFileName;
                                        const isPDF = isPdfContentType(contentType) ||
                                            pdfUrl.includes('application/pdf') ||
                                            reportFileName.includes('.pdf');
                                        const isImage = isImageContentType(contentType) ||
                                            (!isPDF && reportFileName.match(/\.(png|jpe?g|gif|webp|bmp|tiff?)$/));

                                        if (isImage) {
                                            return (
                                                <img
                                                    src={pdfUrl}
                                                    alt={selectedReportName || 'Report preview'}
                                                    style={{
                                                        border: 'none',
                                                        borderRadius: isFullscreen ? '0' : '4px',
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            );
                                        }

                                        if (isDocxPreview) {
                                            if (isDocxPreviewLoading) {
                                                return (
                                                    <StethoscopeLoading
                                                        style={{ height: '100%' }}
                                                    />
                                                );
                                            }

                                            if (docxPreviewError) {
                                                return (
                                                    <div className="d-flex align-items-center justify-content-center h-100">
                                                        <p className="text-danger fw-bold">{docxPreviewError}</p>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div
                                                    className="h-100 bg-white"
                                                    style={{
                                                        overflow: 'auto',
                                                        padding: isFullscreen ? '24px 32px' : '16px 20px',
                                                        borderRadius: isFullscreen ? '0' : '4px'
                                                    }}
                                                >
                                                    <div dangerouslySetInnerHTML={{ __html: docxHtml }} />
                                                </div>
                                            );
                                        }

                                        return (
                                            <iframe
                                                key={pdfUrl}
                                                ref={iframeRef}
                                                className="w-100 h-100"
                                                src={buildPdfPreviewSrc(pdfUrl, { toolbar: hidePdfToolbar ? 0 : 1 })}
                                                title="Report Preview"
                                                style={{
                                                    border: 'none',
                                                    borderRadius: isFullscreen ? '0' : '4px',
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            />
                                        );
                                    })()}
                                </div>
                            ) : (
                                <div className="d-flex align-items-center justify-content-center" style={{ flex: '1', minHeight: 0 }}>
                                    <p className="text-danger fw-bold">{isFromReceipts ? 'Please select a receipt to view' : 'Please select a report to view'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportDisplaySection; 